import { useTranslation } from "react-i18next"
import { ArrowSquareOut, EyedropperSample, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { chLaboratoryPrescriptionDrawerFragment$key } from "@/modules/patients/detail/laboratory-prescription/ch/__generated__/chLaboratoryPrescriptionDrawerFragment.graphql"
import { ChLaboratoryPrescriptionDrawer } from "@/modules/patients/detail/laboratory-prescription/ch/ch-laboratory-prescription-drawer"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface LaboratoryPrescriptionDocumentProps extends DocumentProps {
  id?: string
  progressNoteId: string
  laboratoryPrescriptionRef: chLaboratoryPrescriptionDrawerFragment$key
  medgateConnectOrder?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["medgateConnectOrder"]
}

const LaboratoryPrescriptionDocument = ({
  id,
  progressNoteId,
  laboratoryPrescriptionRef,
  medgateConnectOrder,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
}: LaboratoryPrescriptionDocumentProps) => {
  const { t } = useTranslation()
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="laboratory-prescription-document-item"
      onClick={() => {
        if (!isEditable && id) {
          onSelect(id)
          return
        }
        openDrawer(
          () => (
            <ChLaboratoryPrescriptionDrawer
              id={id}
              progressNoteId={progressNoteId}
              laboratoryPrescriptionRef={laboratoryPrescriptionRef}
              medgateConnectOrder={medgateConnectOrder}
            />
          ),
          "LaboratoryPrescriptionDrawer",
        )
      }}>
      <EyedropperSample
        className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")}
      />
      <div className={cn("ml-2 min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText
          data-testid="summary-prescription-for-laboratory-header"
          className={isSelected ? "text-on-primary-surface" : ""}>
          {t("patient.laboratoryPrescription.prescriptionForMedgateConnect")}
        </ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="prescription-for-laboratory-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="prescription-for-laboratory-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { LaboratoryPrescriptionDocument }
